<template>
  <Teleport to="body">
    <div v-resize="resetPosition" class="call-dialog">
      <v-fade-transition @after-leave="maximize">
        <DraggableResizableVue
          v-show="show"
          v-model:x="draggable.x"
          v-model:y="draggable.y"
          :w="draggable.w"
          :h="draggable.h"
          :resizable="false"
          parent
          class="call-dialog__draggable"
          :class="{ 'call-dialog__draggable--transition-none': isDragging }"
          drag-handle=".call-dialog__handle"
          @dragging="isDragging = true"
          @dragstop="isDragging = false"
        >
          <v-card class="call-dialog__card" :width="draggable.w" :height="draggable.h" elevation="6">
            <div v-if="isMinimized" class="height-100 d-flex justify-center align-center bg-primary">
              <EmIconButton
                icon="mdi-phone-in-talk"
                color="white"
                size="x-large"
                squared
                @click="maximize"
              />
            </div>
            <template v-else>
              <div class="call-dialog__handle d-flex justify-space-between align-center px-4 py-3 bg-primary">
                <div class="mr-2">
                  <EmBody2 semi-bold class="text-no-wrap">
                    {{ title }}
                  </EmBody2>
                </div>
                <div class="d-flex flex-nowrap">
                  <EmIcon icon="mdi-minus" color="white" class="mr-2" @click="minimize" />
                  <EmIcon icon="mdi-close" color="white" @click="$emit('close')" />
                </div>
              </div>
              <EmDivider />
              <div class="d-flex justify-space-between align-end pa-4">
                <InboxConversationHeaderContactInfo
                  v-if="contact"
                  :name="contact.name"
                  :phone-number="contact.phone"
                  data-cy="call-incomming-contact"
                />
                <CallDuration v-if="showDuration" class="ml-2" />
              </div>
              <EmDivider />
              <div class="pa-4">
                <slot name="buttons" />
              </div>
            </template>
          </v-card>
        </DraggableResizableVue>
      </v-fade-transition>
    </div>
  </Teleport>
</template>

<script setup>
import { DraggableResizableVue } from 'draggable-resizable-vue3'
import { debounce } from 'lodash'
import { useWindowSize } from '@vueuse/core'
const { width: windowWidth, height: windowHeight } = useWindowSize()
const show = defineModel({ type: Boolean })
defineProps({
  title: { type: String, required: true },
  contact: { type: [Object, null], default: null },
  showDuration: { type: Boolean, default: false }
})
defineEmits(['close'])
const MAX_W = 400
const MAX_H = 220
const MIN_W = 60
const MIN_H = 60
const getMaxX = () => windowWidth.value / 2 - MAX_W / 2
const getMaxY = () => windowHeight.value / 2 - MAX_H / 2
const getMinX = () => windowWidth.value - MIN_W
const getMinY = () => windowHeight.value / 2 - MIN_H / 2
const draggable = ref({ w: MAX_W, h: MAX_H, x: getMaxX(), y: getMaxY() })
const isDragging = ref(false)
const isMinimized = ref(false)
const maximize = () => {
  isMinimized.value = false
  draggable.value = { w: MAX_W, h: MAX_H, x: getMaxX(), y: getMaxY() }
}
const minimize = () => {
  isMinimized.value = true
  draggable.value = { w: MIN_W, h: MIN_H, x: getMinX(), y: getMinY() }
}
const resetPosition = debounce(() => {
  draggable.value.x = isMinimized.value ? getMinX() : getMaxX()
  draggable.value.y = isMinimized.value ? getMinY() : getMaxY()
}, 100)
</script>

<style lang="scss" scoped>
.call-dialog {
  position: absolute;
  top: 10px;
  bottom: 10px;
  left: 10px;
  right: 10px;
  z-index: 1005;
  user-select: none;
  pointer-events: none;
  &__draggable {
    border: none;
    pointer-events: all;
    transition: all .3s ease;
    &--transition-none {
      transition: none !important;
    }
  }
  &__card {
    transition: all .3s ease;
  }
  &__handle {
    cursor: grab;
    &:active {
      cursor: grabbing;
    }
  }
}
</style>
